import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, ArrowRight, Loader2, CheckCircle, Gift,
  Coffee, Minus, Plus, Trash2, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { api } from '@/utils/apiClient';

/**
 * Cart Recovery Page
 * Restores an abandoned cart from a recovery link
 */
export default function CartRecoveryPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [error, setError] = useState(null);
  const [guestDiscount, setGuestDiscount] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/cart/recover/${token}`);
      const data = res.data;
      
      if (data.success) {
        setCart(data.cart);
        setGuestDiscount(data.guest_discount);
        
        // Store recovery token in sessionStorage for checkout completion tracking
        sessionStorage.setItem('cart_recovery_token', token);
        
        // Save cart to localStorage so it persists
        localStorage.setItem('bulaccino_cart', JSON.stringify(data.cart.items));
        
        // Show discount toast if applicable
        if (data.guest_discount?.enabled) {
          toast.success(`Welcome back! ${data.guest_discount.percent}% discount applied!`, {
            duration: 5000
          });
        }
      } else {
        setError(data.detail || 'Cart not found');
      }
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      setError(err.response?.data?.detail || 'Failed to load cart');
    }
    setLoading(false);
  };

  const updateQuantity = (itemId, delta) => {
    if (!cart) return;
    
    const updatedItems = cart.items.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0);
    
    setCart({ ...cart, items: updatedItems });
    localStorage.setItem('bulaccino_cart', JSON.stringify(updatedItems));
  };

  const removeItem = (itemId) => {
    if (!cart) return;
    
    const updatedItems = cart.items.filter(item => item.id !== itemId);
    setCart({ ...cart, items: updatedItems });
    localStorage.setItem('bulaccino_cart', JSON.stringify(updatedItems));
  };

  const calculateSubtotal = () => {
    if (!cart?.items) return 0;
    return cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    if (!guestDiscount?.enabled) return 0;
    return calculateSubtotal() * (guestDiscount.percent / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const proceedToCheckout = () => {
    // Navigate to order page with cart data
    navigate('/order', { 
      state: { 
        recoveredCart: cart.items,
        recoveryToken: token,
        guestDiscount: guestDiscount?.enabled ? guestDiscount.percent : 0
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your saved cart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-6 flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">Cart Not Found</h1>
            <p className="text-muted-foreground mb-8">
              {error}. This cart may have expired or already been completed.
            </p>
            <Button onClick={() => navigate('/order')} size="lg">
              Start a New Order
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!cart?.items?.length) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 mx-auto mb-6 flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-4">Your Cart is Empty</h1>
            <p className="text-muted-foreground mb-8">
              Looks like your saved items are no longer available.
            </p>
            <Button onClick={() => navigate('/order')} size="lg">
              Browse Our Menu
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Welcome Back!
            </h1>
            <p className="text-muted-foreground">
              We saved your order. Ready to complete it?
            </p>
          </motion.div>

          {/* Discount Banner */}
          {guestDiscount?.enabled && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-2xl p-6 mb-8 text-center"
            >
              <div className="flex items-center justify-center gap-3 mb-2">
                <Gift className="w-6 h-6" />
                <span className="text-lg font-semibold">Special Offer!</span>
              </div>
              <p className="text-3xl font-bold mb-1">{guestDiscount.percent}% OFF</p>
              <p className="text-green-100 text-sm">Applied to your order automatically</p>
            </motion.div>
          )}

          {/* Cart Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl border border-border p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Saved Items ({cart.items.length})
            </h2>
            
            <div className="space-y-4">
              {cart.items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl"
                >
                  {item.image ? (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Coffee className="w-8 h-8 text-amber-600" />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">{item.name}</h3>
                    <p className="text-sm text-muted-foreground">${item.price.toFixed(2)} each</p>
                    {item.customizations && Object.keys(item.customizations).length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {Object.entries(item.customizations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, -1)}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => updateQuantity(item.id, 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-foreground">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 mt-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Remove
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl border border-border p-6 mb-6"
          >
            <h2 className="text-lg font-semibold text-foreground mb-4">Order Summary</h2>
            
            <div className="space-y-3">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </div>
              
              {guestDiscount?.enabled && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Discount ({guestDiscount.percent}%)
                  </span>
                  <span>-${calculateDiscount().toFixed(2)}</span>
                </div>
              )}
              
              <div className="border-t border-border pt-3 flex justify-between text-lg font-bold text-foreground">
                <span>Total</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button 
              onClick={proceedToCheckout}
              size="lg"
              className="w-full h-14 text-lg bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              data-testid="complete-order-btn"
            >
              Complete My Order
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-center text-sm text-muted-foreground mt-4">
              Your cart is saved for 7 days
            </p>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
